<template>
  <div class="knowit-card-list">
    <!-- Batch Selection Controls -->
    <div class="batch-controls">
      <el-button
        v-if="!batchSelectionMode"
        @click="enableBatchSelection"
        size="small"
        icon="el-icon-check"
      >
        Batch Selection
      </el-button>
      <template v-else>
        <el-button
          @click="disableBatchSelection"
          size="small"
        >
          Cancel
        </el-button>
        <el-button
          @click="selectAll"
          size="small"
          type="primary"
        >
          Select All
        </el-button>
        <span class="selected-count">{{ selectedCardIds.length }} selected</span>
      </template>
    </div>

    <!-- Operation Button Bar (shown when cards are selected) -->
    <div v-if="batchSelectionMode && selectedCardIds.length > 0" class="operation-bar">
      <el-button
        @click="batchAddToContext"
        size="small"
        type="primary"
        icon="el-icon-circle-plus"
      >
        Add to Context
      </el-button>
      <el-button
        @click="batchDelete"
        size="small"
        type="danger"
        icon="el-icon-delete"
      >
        Delete
      </el-button>
      <el-button
        @click="batchChangeCollections"
        size="small"
        icon="el-icon-folder"
      >
        Change Collections
      </el-button>
    </div>

    <div v-if="searchLoading" class="loading-container">
      <el-skeleton :rows="3" animated />
    </div>
    
    <div v-else class="cards-grid">
      <SimpleCardView
        v-for="card in displayCards"
        :key="card.id"
        :card="card"
        :collections="collections"
        :is-in-context-pot="isCardInContextPot(card)"
        :can-share="canShare"
        :show-actions="true"
        :show-answer="true"
        :show-reminder="false"
        :batch-selection-mode="batchSelectionMode"
        :is-selected="isCardSelected(card.id)"
        @edit="editCard"
        @delete="deleteCard"
        @delete-success="handleDeleteSuccess"
        @click="handleCardClick"
        @add-to-context="addToContext"
        @share="shareCard"
        @toggle-selection="toggleCardSelection"
        class="card-item"
      />
    </div>

    <div v-if="!searchLoading && displayCards.length === 0" class="empty-state">
      <p>No Knowits found.</p>
      <el-button type="primary" @click="openAddCardDialog">
        Create Your First Knowit
      </el-button>
    </div>

    <!-- Add/Edit Card Dialog -->
    <AddKnowitDialog
      :visible.sync="addCardDialogVisible"
      :card="editingCard"
      :collections="collections"
      @save="saveCard"
      @close="closeAddCardDialog"
    />

    <!-- Card Read View Dialog -->
    <el-dialog
      :visible.sync="readCardDialogVisible"
      width="850px"
      :show-close="true"
      custom-class="card-read-dialog"
      @close="closeReadCardDialog"
    >
      <DirectReadCard
        v-if="selectedCardForReading"
        :card="selectedCardForReading"
        :collections="collections"
        :showAnswer="true"
        :showReminder="true"
      />
    </el-dialog>
  </div>
</template>

<script>
import SimpleCardView from './SimpleCardView.vue'
import DirectReadCard from './DirectReadCard.vue'
import AddKnowitDialog from './AddKnowitDialog.vue'
import { postRequest } from '@/apis/front'

export default {
  name: 'KnowitCardList',
  components: {
    SimpleCardView,
    DirectReadCard,
    AddKnowitDialog
  },
  props: {
    cards: {
      type: Array,
      default: () => []
    },
    collections: {
      type: Array,
      default: () => []
    },
    contextPot: {
      type: Array,
      default: () => []
    },
    canShare: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      addCardDialogVisible: false,
      editingCard: null,
      searchQuery: '',
      searchResults: [],
      searchLoading: false,
      isSearching: false,
      batchSelectionMode: false,
      selectedCardIds: [],
      readCardDialogVisible: false,
      selectedCardForReading: null
    }
  },
  computed: {
    displayCards() {
      return this.isSearching ? this.searchResults : this.cards
    }
  },
  watch: {
    cards: {
      handler() {
        // Clear search when cards are updated
        if (this.isSearching) {
          this.handleClearSearch()
        }
      },
      deep: true
    }
  },
  methods: {
    openAddCardDialog() {
      this.editingCard = null
      this.addCardDialogVisible = true
    },

    editCard(card) {
      this.editingCard = card
      this.addCardDialogVisible = true
    },

    viewCard(card) {
      this.$emit('view-card', card)
    },

    saveCard(cardData) {
      console.log(11111,cardData)
      this.$emit('save-card', cardData)
    },

    deleteCard(card) {
      this.$emit('delete-card', card)
    },
    
    handleDeleteSuccess(cardId) {
      // Pass the delete-success event up to the parent
      this.$emit('delete-success', cardId)
    },

    closeAddCardDialog() {
      this.addCardDialogVisible = false
      this.editingCard = null
    },

    addToContext(card) {
      this.$emit('add-to-context', card)
    },

    shareCard(card) {
      this.$emit('share-card', card)
    },

    isCardInContextPot(card) {
      return this.contextPot.some(potItem => potItem.id === card.id)
    },

    async handleSearch() {
      if (!this.searchQuery.trim()) {
        this.$message.warning('Please enter a search query')
        return
      }

      this.searchLoading = true
      this.isSearching = true
      
      try {
        const response = await postRequest('getRelatedDataFromVectorBase', {
          content: this.searchQuery,
          user_id: this.$store.state.user_id
        })

        if (response.data) {
          // Parse the response if it's a string
          let searchData = response.data
          if (typeof searchData === 'string') {
            try {
              searchData = JSON.parse(searchData)
            } catch (e) {
              console.error('Failed to parse search response:', e)
              searchData = []
            }
          }

          // Process the search results
          if (searchData && searchData.code === 0 && searchData.data) {
            // If the response has a standard format with code and data
            this.searchResults = this.processVectorSearchResults(searchData.data)
          } else if (Array.isArray(searchData)) {
            // If the response is directly an array
            this.searchResults = this.processVectorSearchResults(searchData)
          } else if (searchData.results) {
            // If the response has a results property
            this.searchResults = this.processVectorSearchResults(searchData.results)
          } else {
            // Try to format whatever we got
            this.searchResults = this.formatSearchResults(searchData)
          }
          
          if (this.searchResults.length === 0) {
            this.$message.info('No matching Knowits found')
          } else {
            this.$message.success(`Found ${this.searchResults.length} matching Knowits`)
          }
        } else {
          this.$message.error('Failed to search Knowits')
          this.searchResults = []
        }
      } catch (error) {
        console.error('Error searching:', error)
        this.$message.error('Failed to search Knowits')
        this.searchResults = []
      } finally {
        this.searchLoading = false
      }
    },

    processVectorSearchResults(results) {
      if (!Array.isArray(results)) {
        return []
      }

      // Transform vector search results into card format
      return results.map(item => {
        // Create a card-like object from the search result
        return {
          id: item.id || item._id?.$oid || item._id?.$id || item._id || item.card_id || Math.random().toString(36).substr(2, 9),
          title: item.title || item.name || 'Untitled',
          content: item.content || item.description || '',
          tags: item.tags || [],
          created_at: item.created_at || item.createdAt || new Date().toISOString(),
          updated_at: item.updated_at || item.updatedAt || new Date().toISOString(),
          user_id: item.user_id || this.$store.state.user_id,
          ...item // Include any additional properties from the search result
        }
      })
    },

    formatSearchResults(searchData) {
      // Check if searchData is an array or has a data property
      let results = Array.isArray(searchData) ? searchData : (searchData.data || searchData.results || [])
      
      // Create a map of our cards for quick lookup
      const cardMap = new Map()
      this.cards.forEach(card => {
        // Store by multiple possible ID formats
        if (card.id) cardMap.set(card.id, card)
        if (card._id) {
          if (typeof card._id === 'object' && (card._id.$oid || card._id.$id)) {
            cardMap.set((card._id.$oid || card._id.$id), card)
          } else {
            cardMap.set(card._id, card)
          }
        }
        // Also check by content for vector search matches
        if (card.content) cardMap.set(card.content, card)
      })
      
      // Map the search results to match our card structure
      const matchedCards = []
      results.forEach(item => {
        let matchedCard = null
        
        // Try to find by various ID formats
        if (item.id && cardMap.has(item.id)) {
          matchedCard = cardMap.get(item.id)
        } else if (item._id) {
          const id = typeof item._id === 'object' && (item._id.$oid || item._id.$id) ? (item._id.$oid || item._id.$id) : item._id
          if (cardMap.has(id)) {
            matchedCard = cardMap.get(id)
          }
        } else if (item.card_id && cardMap.has(item.card_id)) {
          matchedCard = cardMap.get(item.card_id)
        }
        
        // If we found a match, add it to results
        if (matchedCard && !matchedCards.some(card => card.id === matchedCard.id)) {
          matchedCards.push(matchedCard)
        }
      })
      
      return matchedCards
    },

    handleClearSearch() {
      this.searchQuery = ''
      this.searchResults = []
      this.isSearching = false
    },

    // Batch selection methods
    enableBatchSelection() {
      this.batchSelectionMode = true
      this.selectedCardIds = []
    },

    disableBatchSelection() {
      this.batchSelectionMode = false
      this.selectedCardIds = []
    },

    selectAll() {
      this.selectedCardIds = this.displayCards.map(card => card.id)
    },

    toggleCardSelection(cardId) {
      const index = this.selectedCardIds.indexOf(cardId)
      if (index > -1) {
        this.selectedCardIds.splice(index, 1)
      } else {
        this.selectedCardIds.push(cardId)
      }
    },

    isCardSelected(cardId) {
      return this.selectedCardIds.includes(cardId)
    },

    handleCardClick(card) {
      console.log('handleCardClick called with card:', card)
      console.log('batchSelectionMode:', this.batchSelectionMode)
      if (this.batchSelectionMode) {
        this.toggleCardSelection(card.id)
      } else {
        this.openReadCardDialog(card)
      }
    },

    openReadCardDialog(card) {
      console.log('openReadCardDialog called with card:', card)
      this.selectedCardForReading = card
      this.readCardDialogVisible = true
      console.log('Dialog should be visible now:', this.readCardDialogVisible)
    },

    closeReadCardDialog() {
      this.readCardDialogVisible = false
      this.selectedCardForReading = null
    },

    // Batch operations
    batchAddToContext() {
      const selectedCards = this.displayCards.filter(card =>
        this.selectedCardIds.includes(card.id)
      )
      this.$emit('batch-add-to-context', selectedCards)
      this.$message.success(`Added ${selectedCards.length} cards to context`)
    },

    batchDelete() {
      this.$confirm(
        `Are you sure you want to delete ${this.selectedCardIds.length} cards?`,
        'Confirm Delete',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(() => {
        const selectedCards = this.displayCards.filter(card =>
          this.selectedCardIds.includes(card.id)
        )
        this.$emit('batch-delete', selectedCards)
        this.selectedCardIds = []
      }).catch(() => {
        this.$message.info('Delete cancelled')
      })
    },

    batchChangeCollections() {
      const selectedCards = this.displayCards.filter(card =>
        this.selectedCardIds.includes(card.id)
      )
      this.$emit('batch-change-collections', selectedCards)
    }
  }
}
</script>

<style scoped>
.knowit-card-list {
  width: 100%;
  height: 100%;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.batch-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}

.selected-count {
  font-size: 14px;
  color: #606266;
  margin-left: 8px;
}

.operation-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: #ecf5ff;
  border-bottom: 1px solid #b3d8ff;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
}

.search-input {
  flex: 0 1 500px;
  max-width: 500px;
  box-sizing: border-box;
}

.add-button {
  flex-shrink: 0;
  margin-left: auto;
}

.search-input :deep(.el-input__inner) {
  height: 40px;
  font-size: 14px;
}

.loading-container {
  padding: 20px;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
  gap: 20px;
  padding: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.card-item {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.empty-state p {
  margin-bottom: 24px;
  font-size: 16px;
  color: #606266;
}

.empty-state .el-button {
  padding: 12px 24px;
  font-size: 14px;
  border-radius: 6px;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
    gap: 16px;
    padding: 16px;
  }
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 12px;
  }
}
</style>

<style>
/* Dialog styling (not scoped to apply to el-dialog) */
.card-read-dialog {
  border-radius: 8px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.card-read-dialog .el-dialog__header {
  padding: 0;
  border-bottom: none;
}

.card-read-dialog .el-dialog__body {
  padding: 0;
  max-height: calc(90vh - 60px);
  overflow-y: auto;
  background: #fafafa;
}

.card-read-dialog .el-dialog__close {
  font-size: 24px;
  color: #606266;
}
</style>